import React from 'react';
import { TableCell, TableRow } from "../../../../components/ui/table";
import { Badge } from "../../../../components/ui/badge";
import { Skeleton } from "../../../../components/ui/skeleton";
import { PodMetricsMap } from '../utils/types';
import { extractPodId, extractDeploymentName } from '../utils/helpers';
import PodActions from './PodActions';
import PodStatus from './PodStatus';

interface PodRowProps {
  pod: any;
  podMetrics: PodMetricsMap;
  podSessionMap?: Map<string, any[]>;
  isLoadingSessions?: boolean;
  showDeployment?: boolean;
  showNode?: boolean;
  showApp?: boolean;
  showCpu?: boolean;
}

const PodRow: React.FC<PodRowProps> = ({
  pod,
  podMetrics,
  podSessionMap = new Map(),
  isLoadingSessions = false,
  showDeployment = true,
  showNode = false,
  showApp = true,
  showCpu = false
}) => {
  const podName = pod.metadata.name;
  const podMetric = podMetrics[podName];
  const memoryUsage = podMetric ? podMetric.memory.usageFormatted : 'N/A';
  const cpuUsage = podMetric ? podMetric.cpu.usage : 'N/A';
  const podSessions = podSessionMap.get(podName) || [];
  const podId = extractPodId(podName);
  const deploymentName = extractDeploymentName(podName);
  
  // Function to determine memory badge color based on usage
  const getMemoryBadgeVariant = (memoryUsage: string): "default" | "secondary" | "destructive" | "outline" => {
    if (!memoryUsage || memoryUsage === 'N/A') return "outline";
    
    // Extract numeric value from string like "4.2 GB"
    const match = memoryUsage.match(/(\d+(\.\d+)?)/);
    if (!match) return "outline";
    
    const memoryGB = parseFloat(match[0]);
    
    if (memoryGB >= 4) return "destructive";
    if (memoryGB >= 3) return "secondary";
    return "outline";
  };

  // Function to render memory badge with appropriate color
  const renderMemoryBadge = (memoryUsage: string) => {
    const variant = getMemoryBadgeVariant(memoryUsage);
    return (
      <Badge variant={variant}>
        {memoryUsage}
      </Badge>
    );
  };

  // Function to format CPU usage by converting nanocores to CPU units
  const formatCpuUsage = (cpuUsage: string): string => {
    if (!cpuUsage || cpuUsage === 'N/A') return 'N/A';
    
    // Convert nanocores (n) to CPU units
    if (cpuUsage.endsWith('n')) {
      // Extract numeric value
      const nanocores = parseInt(cpuUsage.slice(0, -1));
      
      // Convert nanocores to cores (1 core = 1,000,000,000n)
      const cores = nanocores / 1000000000;
      
      // Format based on value size
      if (cores < 0.001) {
        // Show as millicores for very small values
        return `${(cores * 1000).toFixed(2)}m`;
      } else if (cores < 0.1) {
        // Show with 3 decimal places for small values
        return `${cores.toFixed(3)}`;
      } else {
        // Show with 2 decimal places for larger values
        return `${cores.toFixed(2)}`;
      }
    }
    
    // Handle millicores format (e.g., "100m")
    if (cpuUsage.endsWith('m')) {
      return cpuUsage;
    }
    
    // Return as is for values already in CPU units
    return cpuUsage;
  };

  const getSessionStatusBadge = (podSessions: any[]) => {
    if (podSessions.length === 0) {
      return <Badge variant="outline">None</Badge>;
    }
    
    // Get the first session (since there's only one per pod)
    const session = podSessions[0];
    const status = session.status || 'Unknown';
    
    // Return badge with appropriate style based on status
    switch (status.toLowerCase()) {
      case 'connected':
      case 'active':
        return <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-200">{status}</Badge>;
      case 'disconnected':
      case 'inactive':
        return <Badge variant="secondary" className="bg-gray-100 text-gray-800 hover:bg-gray-200">{status}</Badge>;
      case 'error':
      case 'failed':
        return <Badge variant="secondary" className="bg-red-100 text-red-800 hover:bg-red-200">{status}</Badge>;
      case 'connecting':
      case 'pending':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800 hover:bg-amber-200">{status}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <TableRow key={podName} className="hover:bg-slate-100/50">
      <TableCell>
        {isLoadingSessions ? (
          <Skeleton className="h-6 w-10" />
        ) : (
          getSessionStatusBadge(podSessions)
        )}
      </TableCell>
      <TableCell className="font-mono text-xs">{podId}</TableCell>
      <TableCell className="font-medium truncate max-w-[200px]" title={podName}>
        {podName}
      </TableCell>
      <TableCell><PodStatus pod={pod} /></TableCell>
      <TableCell>{renderMemoryBadge(memoryUsage)}</TableCell>
      {showCpu && (
        <TableCell>{formatCpuUsage(cpuUsage)}</TableCell>
      )}
      {showApp && (
        <TableCell>{pod.metadata?.labels?.['app.kubernetes.io/name'] || 'N/A'}</TableCell>
      )}
      <TableCell>{pod.metadata.namespace}</TableCell>
      {showDeployment && (
        <TableCell>{deploymentName}</TableCell>
      )}
      {showNode && (
        <TableCell className="font-mono text-xs truncate">{pod.spec?.nodeName || 'N/A'}</TableCell>
      )}
      <TableCell>
        <PodActions 
          podName={podName}
          namespace={pod.metadata.namespace}
        />
      </TableCell>
    </TableRow>
  );
};

export default PodRow; 